import Link from 'next/link'
import { useRouter } from 'next/router'
import PropTypes from 'prop-types'
import { head, last } from 'ramda'

import Divider from 'components/Divider'
import Spacer from 'components/Spacer'

import { NextLink, PageLink, PrevLink, Root, ViewAllLink } from './Pagination.style'

const Pagination = ({ page: currentPage, totalPages, shallow = false, viewAllEnabled = true }) => {
  const router = useRouter()
  const isPreviousDisabled = currentPage === 1
  const isNextDisabled = currentPage === totalPages
  const previousAriaDisabled = isPreviousDisabled ? 'true' : 'false'
  const nextAriaDisabled = isNextDisabled ? 'true' : 'false'
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/'
  let pages = []

  // Add 2 either side of current page
  for (let i = currentPage - 2; i <= currentPage + 2; i++) {
    if (i > 0 && i <= totalPages) {
      pages.push(i)
    }
  }

  while (pages.length < 5 && last(pages) !== totalPages) {
    pages.push(last(pages) + 1)
  }

  while (pages.length < 5 && head(pages) !== 1) {
    pages.unshift(head(pages) - 1)
  }

  if (totalPages <= 1) return null

  const hrefForPage = (page) => ({ pathname, query: { ...router.query, page } })

  const showViewAll = viewAllEnabled && totalPages <= 20

  return (
    <>
      <Spacer y={6} />
      <Divider />
      <Spacer y={6} />
      <Root>
        {!isPreviousDisabled && (
          <Link href={hrefForPage(currentPage - 1)} passHref shallow={shallow} legacyBehavior>
            <PrevLink
              tabIndex={isPreviousDisabled ? '-1' : '0'}
              role="button"
              aria-disabled={previousAriaDisabled}
              aria-label="Previous page"
              rel="prev"
            >
              Prev
            </PrevLink>
          </Link>
        )}
        {pages.map((page) => {
          const isCurrent = page === currentPage
          return (
            <Link key={page} href={hrefForPage(page)} passHref shallow={shallow} scroll legacyBehavior>
              <PageLink
                tabIndex="0"
                role="button"
                aria-label={`Go to page ${page}`}
                aria-current={isCurrent}
                $isCurrent={isCurrent}
              >
                {page}
              </PageLink>
            </Link>
          )
        })}
        {!isNextDisabled && (
          <Link href={hrefForPage(currentPage + 1)} passHref shallow={shallow} legacyBehavior>
            <NextLink
              tabIndex={isNextDisabled ? '-1' : '0'}
              role="button"
              aria-disabled={nextAriaDisabled}
              aria-label="Next page"
              rel="next"
            >
              Next
            </NextLink>
          </Link>
        )}
      </Root>
      {showViewAll && (
        <>
          <Spacer y={2} />
          <Link href={hrefForPage('all')} passHref shallow={shallow} legacyBehavior>
            <ViewAllLink tabIndex={0} role="button" aria-label="View all">
              View all
            </ViewAllLink>
          </Link>
        </>
      )}
      <Spacer y={6} />
    </>
  )
}

Pagination.propTypes = {
  page: PropTypes.number,
  totalPages: PropTypes.number,
}

Pagination.defaultProps = {
  page: 1,
  totalPages: 1,
}

export default Pagination
